import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { parseServerDate } from '../utils/formatDate';

export default function Respondents({ showToast }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pageData = location.state || {};
  const { authFetch } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selCompany, setSelCompany] = useState(pageData?.companyId || '');
  const [rounds, setRounds] = useState([]);
  const [selRound, setSelRound] = useState('');
  const [respondents, setRespondents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deptFilter, setDeptFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');

  useEffect(() => {
    authFetch('/api/companies').then(r => r.json()).then(d => setCompanies(Array.isArray(d) ? d : []));
  }, [authFetch]);

  useEffect(() => {
    if (!selCompany) return;
    authFetch(`/api/assessments/rounds/${selCompany}`).then(r => r.json()).then(d => {
      if (Array.isArray(d)) { setRounds(d); if (d.length > 0) setSelRound(''); }
    });
  }, [selCompany, authFetch]);

  useEffect(() => { if (selCompany) load(); }, [selCompany, selRound]);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams({ company_id: selCompany });
    if (selRound) params.set('round_id', selRound);
    const res = await authFetch(`/api/assessments?${params}`);
    const d = await res.json();
    setRespondents(Array.isArray(d) ? d : []);
    setLoading(false);
  };

  const deleteRespondent = async (id, name) => {
    if (!window.confirm(`Delete response from "${name}"? This affects the report scores.`)) return;
    const res = await authFetch(`/api/assessments/${id}`, { method: 'DELETE' });
    if (res.ok) { showToast('Response deleted'); load(); }
    else showToast('Delete failed', 'error');
  };

  const addRound = async () => {
    const label = window.prompt('New round label (e.g. "Q2 2025"):');
    if (!label) return;
    const res = await authFetch(`/api/assessments/rounds/${selCompany}`, { method: 'POST', body: JSON.stringify({ label }) });
    if (res.ok) {
      showToast(`Round "${label}" created`);
      const d = await res.json();
      setRounds(prev => [...prev, d]);
    } else showToast('Failed to create round', 'error');
  };

  const closeRound = async (roundId, label) => {
    if (!window.confirm(`Close "${label}"? No more responses can be collected.`)) return;
    const res = await authFetch(`/api/assessments/rounds/${roundId}/close`, { method: 'PUT' });
    if (res.ok) {
      showToast(`Round "${label}" closed`);
      setRounds(prev => prev.map(r => r.id === roundId ? { ...r, status: 'closed' } : r));
    } else showToast('Failed', 'error');
  };

  const filtered = respondents.filter(r => {
    if (deptFilter && r.department !== deptFilter) return false;
    if (levelFilter && r.level !== levelFilter) return false;
    return true;
  });

  const depts = [...new Set(respondents.map(r => r.department).filter(Boolean))];
  const levels = [...new Set(respondents.map(r => r.level).filter(Boolean))];

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Respondent Management</div>
        <div className="page-sub">View, filter, and manage individual assessment responses</div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="g2">
          <div>
            <label className="form-label">Company</label>
            <select className="form-select" value={selCompany} onChange={e => { setSelCompany(e.target.value); setRespondents([]); }}>
              <option value="">Select company...</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {rounds.length > 0 && (
            <div>
              <label className="form-label">Audit Round</label>
              <select className="form-select" value={selRound} onChange={e => setSelRound(e.target.value)}>
                <option value="">All rounds</option>
                {rounds.map(r => <option key={r.id} value={r.id}>{r.label} ({r.response_count || 0} responses) {r.status === 'closed' ? '✓ Closed' : '● Collecting'}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      {selCompany && (
        <>
          {/* Rounds management */}
          <div className="sec-header">
            <div className="sec-title">Audit Rounds</div>
            <div className="sec-line"></div>
            <button className="btn btn-primary btn-sm" onClick={addRound}>+ New Round</button>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
            {rounds.length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)' }}>No rounds yet — one will be auto-created when the first response is submitted.</div>}
            {rounds.map(r => (
              <div key={r.id} className="card" style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', borderColor: r.status === 'collecting' ? 'rgba(34,197,94,0.3)' : 'var(--border)' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{r.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{r.response_count || 0} responses · {r.status === 'collecting' ? <span style={{ color: 'var(--green)' }}>● Collecting</span> : <span style={{ color: 'var(--text3)' }}>✓ Closed</span>}</div>
                </div>
                <div style={{ display: 'flex', gap: 7 }}>
                  <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => navigate('/report', { state: { companyId: selCompany, roundId: r.id } })}>Report →</button>
                  {r.status === 'collecting' && <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--orange)', borderColor: 'rgba(249,115,22,0.3)' }} onClick={() => closeRound(r.id, r.label)}>Close</button>}
                </div>
              </div>
            ))}
          </div>

          {/* Respondents table */}
          <div className="sec-header">
            <div className="sec-title">Responses ({filtered.length})</div>
            <div className="sec-line"></div>
            {depts.length > 1 && (
              <select className="form-select" style={{ maxWidth: 140 }} value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
                <option value="">All depts</option>
                {depts.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            )}
            {levels.length > 1 && (
              <select className="form-select" style={{ maxWidth: 140, marginLeft: 8 }} value={levelFilter} onChange={e => setLevelFilter(e.target.value)}>
                <option value="">All levels</option>
                {levels.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            )}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="loader" style={{ margin: '0 auto' }}></div></div>
          ) : filtered.length === 0 ? (
            <div className="card empty-state">
              <div className="empty-icon">📋</div>
              <div className="empty-title">No responses yet</div>
              <div className="empty-sub">Share the assessment link to collect responses from your team</div>
              <button className="btn btn-primary" onClick={() => navigate('/assessment', { state: { companyId: selCompany } })}>Get Assessment Link →</button>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="table">
                <thead><tr><th>Name</th><th>Department</th><th>Level</th><th>Round</th><th>Submitted</th><th>Action</th></tr></thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 600, color: 'var(--text)' }}>{r.respondent_name || 'Anonymous'}</td>
                      <td>{r.department ? <span className="tag" style={{ fontSize: 11 }}>{r.department}</span> : '—'}</td>
                      <td><span className="tag" style={{ fontSize: 11 }}>{r.level || '—'}</span></td>
                      <td style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--ff-m)' }}>
                        {rounds.find(round => round.id === r.round_id)?.label || '—'}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--text3)' }}>
                        {parseServerDate(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td>
                        <button className="btn btn-danger btn-sm" style={{ fontSize: 11 }} onClick={() => deleteRespondent(r.id, r.respondent_name || 'Anonymous')}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text3)' }}>
            {respondents.length} total responses · Deleting a response recalculates the report scores
          </div>
        </>
      )}
    </div>
  );
}
